// React and libraries imports
import { useState, useRef, useMemo, useEffect } from "react";

// Context and hooks imports
import usePages from "../../../Hooks/Display/usePages";
import { usePropsStoreState } from "../../../Hooks/PropStore/usePropsStore";
// Components imports
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { ComboBox, DropDownList } from "@progress/kendo-react-dropdowns";
import InnerLoader from "../../Shared/Loader/InnerLoader";

// Util and other imports
import PageDataTypes from "./PageTypes";
import { checkDuplicatePageName, getFlatPages, getHighestPageOrder } from "../utils";
import { createPage } from "../../../API/Display/Display";
import { ADD_PAGE, TOGGLE_ADD_NEW_PAGE_DIALOG } from "../../../Context/Display/Pages/PagesContextEvents";
import { getPortals } from "../../../API/Portal/Portal";

function NewPageDialog({
    onClose
}) {

    const props = usePropsStoreState();
    const [{ pages }, dispatchPageEvent] = usePages();

    const [pageType, setPageType] = useState(PageDataTypes[0]);
    const [subPageType, setSubPageType] = useState(pageType.subPageTypes[0]);
    const [pageTemplate, setPageTemplate] = useState("");
    const [pageName, setPageName] = useState("");
    const [folder, setFolder] = useState("");
    const [creatingPage, setCreatingPage] = useState(false);

    const allPages = useMemo(() => getFlatPages(pages), [pages])

    const createPageButton = useRef(null);

    const isPageNameUsed = checkDuplicatePageName(allPages, pageName);
    const pageNameHasSpecialCharacters = pageName.match(/[^a-zA-Z0-9 ]/g);

    function handleChange(event) {
        const { name, value } = event.target;
        debugger
        switch (name) {
            case "Page Type":
                setPageType(value);
                setSubPageType(value.subPageTypes[0]);
                if (value.value === "Linked Record") {
                    setPageTemplate(value.subPageTypes[0].pageTemplates[0])
                } else {
                    setPageTemplate("");
                }
                break;
            case "Sub Page Type":
                setSubPageType(value);
                setPageTemplate(value.pageTemplates[0]);
                setPageName(value.text);
                break;
            case "Page Template":
                setPageTemplate(value);
                break;
            case "Page Name":
                setPageName(value);
                break;
            case "Folder":
                setFolder(value);
                break;
            default:
                break;
        }
    }

    function isSubmitDisabled() {
        return (
            pageName.trim().length === 0 ||
            pageNameHasSpecialCharacters ||
            isPageNameUsed ||
            !folder ||
            !folder.id ||
            creatingPage ||
            (pageType.value === "Linked Record Reference" && !pageTemplate)
        )
    }

    async function handleSubmit(event) {
        event.preventDefault();
        debugger;

        const { entityId, projectId } = props;

        const properties = {
            pageType: pageType.value,
            subPageType: subPageType.value,
            pageTemplate: pageTemplate,
            pageName: pageName.trim(),
            useAsReference: subPageType.value.startsWith("Link"),
            order: getHighestPageOrder(folder.items) + 10000000000,
            parentId: folder.id,
            portalId: folder.id,
            entityId,
            projectId
        }

        if (properties.pageType !== "Entity Specific") {
            properties.pageType = "Entity Group";

            if (properties.subPageType === "Link Record Table") {
                properties.subPageType = "Link Record";
            } else if (pageType.value === "Linked Record") {
                properties.useAsReference = true;
                properties.subPageType = properties.pageTemplate.trim();
            }

            if (
                properties.subPageType ===
                "Linked Record All Records Table"
            ) {
                properties.showsRecordDetails = true;
            }
        }
        const page = await createPage(entityId, properties);

        dispatchPageEvent({
            type: ADD_PAGE,
            payload: {
                id: page.pageId,
                ...properties,
                components: []
            }
        })

        resetProperties()

        closeDialog();
    }

    function resetProperties() {
        setPageType(PageDataTypes[0]);
        setSubPageType(pageType.subPageTypes[0]);
        setPageTemplate("");
        setPageName("");
        setFolder("");
        setCreatingPage(false);
    }

    function closeDialog() {
        dispatchPageEvent({
            type: TOGGLE_ADD_NEW_PAGE_DIALOG
        });
    }

    return (
        <Dialog
            width={"35%"}
            autoFocus={false}
            title={"Create New Page"}
            onClose={closeDialog}
        >
            <form onSubmit={handleSubmit} className="container-fluid">
                <div className="row">
                    <div className="col-12 form-group">
                        <div className="form-label">Page Type</div>
                        <ComboBox
                            name="Page Type"
                            placeholder="select page type"
                            textField="text"
                            dataItemKey="value"
                            value={pageType}
                            className="form-control-custom"
                            data={PageDataTypes}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-12 form-group">
                        <div className="form-label">Sub Page Type</div>
                        <ComboBox
                            name="Sub Page Type"
                            placeholder="Select sub page type"
                            value={subPageType}
                            textField="text"
                            dataItemKey="value"
                            className="form-control-custom"
                            data={pageType.subPageTypes}
                            onChange={handleChange}
                        />
                    </div>
                    {subPageType.pageTemplates.length > 0 && (
                        <div className="col-12 form-group">
                            <div className="form-label">Page Template</div>
                            <ComboBox
                                name="Page Template"
                                placeholder="Select page template"
                                value={pageTemplate}
                                className="form-control-custom"
                                data={subPageType.pageTemplates}
                                onChange={handleChange}
                                disabled={subPageType.value === "Linked Record All Records"}
                            />
                        </div>
                    )}
                    <div className="col-12 form-group">
                        <div className="d-flex align-items-center">
                            <span className="me-1 form-label">Name</span>
                            {isPageNameUsed && <small className="text-danger">This name is already in use</small>}
                            {pageNameHasSpecialCharacters && <small className="text-danger">This name is not allowed</small>}
                        </div>
                        <input
                            maxLength="64"
                            className="form-control w-100 name-text"
                            name="Page Name"
                            onChange={handleChange}
                            value={pageName}
                        />
                    </div>
                    <div className="col-12 form-group">
                        <div className="mb-1">
                            <div className="d-flex align-items-center">
                                <span className="me-1 form-label">Folder</span>
                            </div>
                            <DropDownList
                                name="Folder"
                                placeholder="Enter Folder name"
                                value={folder}
                                className="form-control-custom"
                                data={pages}
                                onChange={handleChange}
                                textField={"text"}
                                valueField={"id"}
                            />
                        </div>
                    </div>
                </div>
                <DialogActionsBar layout="end">
                    {creatingPage && (
                        <small className="mt-2">
                            <div className="d-inline-block">
                                <InnerLoader borderColor="green" />
                                <span className="me-4 text-success"></span>
                            </div>
                        </small>
                    )}
                    <button
                        type={'button'}
                        onClick={closeDialog}
                        className="btn btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        type={'submit'}
                        ref={createPageButton}
                        onClick={handleSubmit}
                        className="btn btn-primary"
                        disabled={isSubmitDisabled()}
                    >
                        Create
                    </button>
                </DialogActionsBar>
            </form>
        </Dialog>
    )
}

export default NewPageDialog;